/**
 * Same idea as Squares `filterToCountedBallResults` — legal balls only (skip wides / no-balls /
 * penalties / empty). Kept local so this util stays free of component imports.
 * @param {unknown[]} raw
 * @returns {number}
 */
function countedBalls(raw) {
  if (!Array.isArray(raw)) return 0;
  let n = 0;
  for (const item of raw) {
    if (item == null) continue;
    const s = String(item).trim();
    if (!s) continue;
    const lower = s.toLowerCase();
    if (lower.includes("pen")) continue;
    if (lower.includes("wide") || /(^|[^a-z])wd([^a-z]|$)/.test(lower)) continue;
    if (/^[1-7]w$/i.test(lower)) continue;
    if (/^w\+[1-5]w$/i.test(lower)) continue;
    if (/^[1-8]nb$/i.test(lower)) continue;
    if (/no\s*ball|(^|[^a-z])nb([^a-z]|$)/.test(lower)) continue;
    n += 1;
    if (n >= 6) break;
  }
  return n;
}

/**
 * Same “current over” row selection as {@link applyScoregridFromParsed} in DecimalSocketContext:
 * highest innings (4→1), then last over in that array with bowler set.
 * @param {any} sg scoregrid msg_data with `.overs.inns1` … `inns4`
 * @returns {{ inns: number, ov: number, delivery: any[], rowIndex: number, innsRows: any[] } | null}
 */
function findLatestBowlingRow(sg) {
  if (!sg?.overs || typeof sg.overs !== "object") return null;
  for (let i = 4; i >= 1; i--) {
    const thisInns = sg.overs["inns" + i];
    if (!Array.isArray(thisInns) || thisInns.length === 0) continue;
    for (let ov = thisInns.length - 1; ov >= 0; ov--) {
      const row = thisInns[ov];
      if (row && row.bowler !== "" && row.bowlerid !== "") {
        return {
          inns: i,
          ov: Number(row.ov),
          delivery: deliveryFromRow(row),
          rowIndex: ov,
          innsRows: thisInns,
        };
      }
    }
  }
  return null;
}

function deliveryFromRow(row) {
  return [
    row.b1,
    row.b2,
    row.b3,
    row.b4,
    row.b5,
    row.b6,
    row.b7,
    row.b8,
    row.b9,
    row.b10,
    row.b11,
    row.b12,
  ];
}

/**
 * Same “current over” row selection as {@link applyScoregridFromParsed} in DecimalSocketContext:
 * highest innings (4→1), then last over in that array with bowler set.
 * @param {any} sg scoregrid msg_data with `.overs.inns1` … `inns4`
 * @returns {{ inns: number, ov: number, delivery: any[] } | null}
 */
export function extractLatestBowlingRowFromScoregrid(sg) {
  const found = findLatestBowlingRow(sg);
  if (!found) return null;
  return { inns: found.inns, ov: found.ov, delivery: found.delivery };
}

/**
 * Live bat/bowl delivery strip: prefer a just-finished over when the feed has already opened
 * the next over's empty shell. Otherwise ball 6's FOUR/SIX/OUT reaction is skipped — the
 * strip's `overKey` jumps before the outcome animation can fire.
 *
 * Hold while the latest bowler-set row has 0 counted balls and the previous row in the same
 * innings already has 6. Release as soon as the new over records a counted ball.
 *
 * @param {any} sg scoregrid msg_data with `.overs.inns1` … `inns4`
 * @returns {{ inns: number, ov: number, delivery: any[] } | null}
 */
export function extractLiveDeliveryBowlingRowFromScoregrid(sg) {
  const latest = findLatestBowlingRow(sg);
  if (!latest) return null;

  const latestCounted = countedBalls(latest.delivery);
  if (latestCounted > 0 || latest.rowIndex <= 0) {
    return { inns: latest.inns, ov: latest.ov, delivery: latest.delivery };
  }

  const prevRow = latest.innsRows[latest.rowIndex - 1];
  if (!prevRow || prevRow.bowler === "" || prevRow.bowlerid === "") {
    return { inns: latest.inns, ov: latest.ov, delivery: latest.delivery };
  }

  const prevDelivery = deliveryFromRow(prevRow);
  if (countedBalls(prevDelivery) < 6) {
    return { inns: latest.inns, ov: latest.ov, delivery: latest.delivery };
  }

  return {
    inns: latest.inns,
    ov: Number(prevRow.ov),
    delivery: prevDelivery,
  };
}
